import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/Combobox";
import { todayISO } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Home, Plus, Trash2, Users, Wrench, ShieldAlert, ClipboardList, Mail, Loader2, MapPin, DollarSign, Star, Ban,
} from "lucide-react";

const SIL_TYPES = ["SIL", "SDA", "STA / MTA", "Other"];
const TYPE_COLOUR = {
  SIL: "bg-rose-50 text-[#bf3b4f] border-rose-200",
  SDA: "bg-purple-50 text-purple-700 border-purple-200",
  "STA / MTA": "bg-amber-50 text-amber-700 border-amber-200",
  Other: "bg-slate-100 text-slate-600 border-slate-200",
};

const EMPTY = {
  property_name: "", status: "Active", sil_type: "SIL", manager: null, num_rooms: 0,
  address_line1: "", suburb: "", state: "", postcode: "", weekly_rent: 0,
  description: "", notes: "", tenancies: [], maintenance: [], whs: [],
  inspections: [], correspondence: [], preferred_staff: [], incompatible_staff: [],
};

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

/* Generic editable child table */
function ChildTable({ rows, cols, onChange, addLabel, testid }) {
  const add = () => onChange([...rows, Object.fromEntries(cols.map((c) => [c.key, c.default ?? ""]))]);
  const update = (i, key, val) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <div className="text-xs text-slate-400 italic py-3 text-center border border-dashed rounded-md">No entries yet.</div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 items-end border border-slate-100 rounded-md p-2 bg-slate-50/60"
             style={{ gridTemplateColumns: `${cols.map((c) => c.w || "1fr").join(" ")} auto` }}
             data-testid={`${testid}-row-${i}`}>
          {cols.map((c) => (
            <div key={c.key} className="space-y-1 min-w-0">
              <Label className="text-[10px] text-slate-400">{c.label}</Label>
              {c.type === "select" ? (
                <Select value={r[c.key] || undefined} onValueChange={(v) => update(i, c.key, v)}>
                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{c.options.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : c.type === "participant" ? (
                <Combobox options={c.options} value={r[c.key] || null}
                          onChange={(v) => { update(i, c.key, v); if (c.onPick) c.onPick(i, v); }}
                          placeholder="Participant" testid={`${testid}-participant-${i}`} />
              ) : (
                <Input type={c.type || "text"} value={r[c.key] ?? ""} className="h-8 text-xs bg-white"
                       onChange={(e) => update(i, c.key, c.type === "number" ? Number(e.target.value) : e.target.value)} />
              )}
            </div>
          ))}
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="text-xs h-8" data-testid={`${testid}-add`}>
        <Plus className="h-3.5 w-3.5 mr-1" /> {addLabel}
      </Button>
    </div>
  );
}

export default function Properties() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null); // form object
  const [editName, setEditName] = useState(null); // existing doc name or null (new)
  const [saving, setSaving] = useState(false);
  const [delName, setDelName] = useState(null);
  const [q, setQ] = useState("");
  const [silCharges, setSilCharges] = useState({}); // participant -> charge form
  const [itemGroups, setItemGroups] = useState([]);
  const [groupItems, setGroupItems] = useState({}); // participant -> [items under its selected group]
  const [silSaving, setSilSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setList(await api.properties()); } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.participants().then((d) => setParticipants(d)).catch(() => {});
    api.staff().then((d) => setStaff(d)).catch(() => {});
    api.itemGroups().then((d) => setItemGroups(d || [])).catch(() => {});
  }, []);

  const silGroupOpts = useMemo(() => {
    const weekly = itemGroups.filter((g) => g.sil_weekly).map((g) => ({ value: g.name, label: g.name }));
    return weekly.length ? weekly : itemGroups.map((g) => ({ value: g.name, label: g.name }));
  }, [itemGroups]);

  const blankSil = () => ({ active: true, item_group: "", item_code: "", item_name: "",
    weekly_amount: 0, ratio: "1:1", start_date: "", end_date: "", billed_periods: [] });
  const setSil = (pid, key, val) => setSilCharges((m) => ({ ...m, [pid]: { ...(m[pid] || blankSil()), [key]: val } }));

  // Load NDIS line items under a participant's selected SIL weekly item group.
  // Auto-selects when there's a single in-date item (and prefills amount if unset).
  const loadGroupItems = async (pid, group) => {
    if (!group) { setGroupItems((m) => ({ ...m, [pid]: [] })); return; }
    try {
      const items = await api.groupItems(group);
      setGroupItems((m) => ({ ...m, [pid]: items }));
      const pick = items.filter((i) => i.in_date).length ? items.filter((i) => i.in_date) : items;
      if (pick.length === 1) {
        const it = pick[0];
        setSilCharges((m) => {
          const c = { ...(m[pid] || blankSil()) };
          c.item_group = group; c.item_code = it.item_code; c.item_name = it.item_name || "";
          if (!(Number(c.weekly_amount) > 0) && it.rate != null) c.weekly_amount = it.rate;
          return { ...m, [pid]: c };
        });
      }
    } catch { setGroupItems((m) => ({ ...m, [pid]: [] })); }
  };

  const partOpts = useMemo(() => participants.map((p) => ({ value: p.name, label: p.display_name || p.name })), [participants]);
  const staffOpts = useMemo(() => staff.map((s) => ({ value: s.name, label: s.display_name || s.name })), [staff]);
  const partName = (name) => participants.find((p) => p.name === name)?.display_name || name;

  const openNew = () => { setEditName(null); setEditing({ ...EMPTY }); setSilCharges({}); };
  const openEdit = async (name) => {
    try {
      const doc = await api.property(name);
      setEditName(name);
      setEditing({
        ...EMPTY, ...doc,
        tenancies: doc.tenancies || [], maintenance: doc.maintenance || [],
        whs: doc.whs || [], inspections: doc.inspections || [], correspondence: doc.correspondence || [],
        preferred_staff: (doc.preferred_staff || []).map((r) => r.staff).filter(Boolean),
        incompatible_staff: (doc.incompatible_staff || []).map((r) => r.staff).filter(Boolean),
      });
      const charges = can.has("invoicing.view") ? await api.silCharges(name).catch(() => []) : [];
      const map = {};
      (charges || []).forEach((c) => { map[c.participant] = { ...blankSil(), ...c, active: !!c.active }; });
      setSilCharges(map);
      setGroupItems({});
      (charges || []).filter((c) => c.item_group).forEach((c) => loadGroupItems(c.participant, c.item_group));
    } catch { toast.error("Could not load property"); }
  };

  const saveSilBilling = async () => {
    const tenants = (editing.tenancies || []).filter((t) => t.participant);
    setSilSaving(true);
    try {
      for (const t of tenants) {
        const c = silCharges[t.participant];
        if (!c) continue;
        if (!c.start_date || !(Number(c.weekly_amount) > 0)) {
          // skip rows left blank; only save configured ones
          if (c.start_date || Number(c.weekly_amount) > 0) { toast.error(`${partName(t.participant)}: needs both a Bill-from date and amount`); setSilSaving(false); return; }
          continue;
        }
        await api.saveSilCharge(t.participant, {
          property: editName, active: !!c.active, item_group: c.item_group || "",
          item_code: c.item_code || "", item_name: c.item_name || c.item_group || "",
          weekly_amount: Number(c.weekly_amount) || 0, ratio: c.ratio || "1:1",
          start_date: c.start_date, end_date: c.end_date || null,
        });
      }
      toast.success("SIL weekly billing saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save SIL billing"); }
    finally { setSilSaving(false); }
  };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const errMsg = (e) => {
    const d = e.response?.data?.detail;
    if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join("; ");
    if (typeof d === "string") return d;
    return "Save failed";
  };
  // Drop empty-string values so Pydantic does not reject blank optional fields.
  const clean = (rows) => (rows || []).map((r) =>
    Object.fromEntries(Object.entries(r).filter(([, v]) => v !== "" && v !== undefined && v !== null)));

  const toggleSilStaff = (listKey, otherKey, id) => {
    setEditing((e) => {
      const cur = new Set(e[listKey] || []);
      const other = new Set(e[otherKey] || []);
      if (cur.has(id)) cur.delete(id);
      else { cur.add(id); other.delete(id); }  // a worker can't be both
      return { ...e, [listKey]: [...cur], [otherKey]: [...other] };
    });
  };

  const save = async () => {
    if (!editing.property_name.trim()) { toast.error("Property name is required"); return; }
    for (const t of (editing.tenancies || [])) if (!t.participant) { toast.error("Every tenancy row needs a participant"); return; }
    for (const m of (editing.maintenance || [])) if (!(m.description || "").trim()) { toast.error("Every maintenance row needs a description"); return; }
    for (const w of (editing.whs || [])) if (!(w.item || "").trim()) { toast.error("Every WHS row needs an item / hazard"); return; }
    for (const c of (editing.correspondence || [])) if (!(c.subject || "").trim()) { toast.error("Every correspondence row needs a subject"); return; }
    setSaving(true);
    try {
      const body = {
        ...editing,
        tenancies: clean((editing.tenancies || []).map((t) => ({ ...t, participant_name: partName(t.participant) }))),
        maintenance: clean(editing.maintenance),
        whs: clean(editing.whs),
        inspections: clean(editing.inspections),
        correspondence: clean(editing.correspondence),
      };
      if (editName) { const r = await api.updateProperty(editName, body); if (r?.auto_unassigned_count) toast.warning(`Property updated. ${r.auto_unassigned_count} upcoming shift(s) auto-unassigned — worker now incompatible with this SIL.`); else toast.success("Property updated"); }
      else { await api.createProperty(body); toast.success("Property created"); }
      setEditing(null); setEditName(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.deleteProperty(delName); toast.success("Property deleted"); setDelName(null); setEditing(null); setEditName(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = list.filter((p) =>
    !q || (p.property_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (p.suburb || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="properties-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Home className="h-5 w-5 text-[#bf3b4f]" /> Properties (SIL)
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Supported Independent Living houses — tenancy, maintenance, WHS, inspections & correspondence.</p>
        </div>
        {can.has("properties.edit") && (
          <Button onClick={openNew} className="text-xs h-9" data-testid="property-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New Property
          </Button>
        )}
      </div>

      <Input placeholder="Search by name or suburb…" value={q} onChange={(e) => setQ(e.target.value)}
             className="h-9 text-xs mb-4 max-w-sm bg-white" data-testid="property-search" />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-sm text-slate-400 border border-dashed rounded-lg">
          No properties yet. {can.has("properties.edit") && "Click “New Property” to add a SIL house."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <button key={p.name} onClick={() => openEdit(p.name)} data-testid={`property-card-${p.name}`}
                    className="text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-rose-300 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-sm text-slate-900 truncate">{p.property_name}</div>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${TYPE_COLOUR[p.sil_type] || TYPE_COLOUR.Other}`}>{p.sil_type}</Badge>
              </div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {[p.suburb, p.state].filter(Boolean).join(", ") || "No address"}
              </div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {p.tenant_count || 0} tenants</span>
                <span className="flex items-center gap-1"><Home className="h-3 w-3" /> {p.num_rooms || 0} rooms</span>
                {p.status !== "Active" && <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500">Inactive</Badge>}
              </div>
              {p.manager_name && <div className="text-[11px] text-slate-400 mt-2 truncate">Manager: {p.manager_name}</div>}
            </button>
          ))}
        </div>
      )}

      {/* Editor */}
      <Sheet open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditName(null); } }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="property-editor">
          {editing && (
            <>
              <SheetHeader className="px-6 py-4 border-b sticky top-0 bg-white z-10">
                <SheetTitle className="text-base flex items-center gap-2">
                  <Home className="h-4 w-4 text-[#bf3b4f]" />
                  {editName ? editing.property_name : "New Property"}
                </SheetTitle>
              </SheetHeader>
              <div className="px-6 py-4">
                <Tabs defaultValue="details">
                  <TabsList className="grid grid-cols-8 w-full h-auto">
                    <TabsTrigger value="details" className="text-[11px] py-1.5" data-testid="tab-details">Details</TabsTrigger>
                    <TabsTrigger value="tenancy" className="text-[11px] py-1.5" data-testid="tab-tenancy"><Users className="h-3 w-3 mr-0.5" />Tenancy</TabsTrigger>
                    <TabsTrigger value="workers" className="text-[11px] py-1.5" data-testid="tab-workers"><Star className="h-3 w-3 mr-0.5" />Workers</TabsTrigger>
                    <TabsTrigger value="sil" className="text-[11px] py-1.5" data-testid="tab-sil"><DollarSign className="h-3 w-3 mr-0.5" />SIL Billing</TabsTrigger>
                    <TabsTrigger value="maint" className="text-[11px] py-1.5" data-testid="tab-maint"><Wrench className="h-3 w-3 mr-0.5" />Maint.</TabsTrigger>
                    <TabsTrigger value="whs" className="text-[11px] py-1.5" data-testid="tab-whs"><ShieldAlert className="h-3 w-3 mr-0.5" />WHS</TabsTrigger>
                    <TabsTrigger value="insp" className="text-[11px] py-1.5" data-testid="tab-insp"><ClipboardList className="h-3 w-3 mr-0.5" />Insp.</TabsTrigger>
                    <TabsTrigger value="corr" className="text-[11px] py-1.5" data-testid="tab-corr"><Mail className="h-3 w-3 mr-0.5" />Corr.</TabsTrigger>
                  </TabsList>

                  <TabsContent value="workers" className="mt-4 space-y-4" data-testid="workers-tab">
                    <div className="flex items-start gap-2 rounded-lg bg-indigo-50 border border-indigo-100 p-2.5 text-[11px] text-indigo-800">
                      <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>These apply to <b>every shift at this SIL house</b>, in addition to each participant's own lists. A preferred worker sees this SIL's open shifts on their job board even if they're not on any resident's team, and sees every tenant with an Active tenancy and an Active participant record under Participants in the worker app. Adding a worker to <b>Incompatible</b> blocks them and auto-unassigns them from this house's upcoming shifts.</span>
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-amber-500" />Preferred workers</div>
                      <div className="space-y-1 max-h-56 overflow-y-auto pr-1 border border-slate-100 rounded-md p-2">
                        {staff.map((s) => (
                          <label key={s.name} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer py-0.5" data-testid={`sil-pref-${s.name}`}>
                            <input type="checkbox" className="accent-amber-500" checked={(editing.preferred_staff || []).includes(s.name)}
                              onChange={() => toggleSilStaff("preferred_staff", "incompatible_staff", s.name)} />
                            {s.display_name || s.name}
                          </label>
                        ))}
                        {staff.length === 0 && <div className="text-[11px] text-slate-400">No staff found.</div>}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Ban className="h-3.5 w-3.5 text-rose-500" />Incompatible workers</div>
                      <div className="space-y-1 max-h-56 overflow-y-auto pr-1 border border-slate-100 rounded-md p-2">
                        {staff.map((s) => (
                          <label key={s.name} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer py-0.5" data-testid={`sil-incompat-${s.name}`}>
                            <input type="checkbox" className="accent-[#bf3b4f]" checked={(editing.incompatible_staff || []).includes(s.name)}
                              onChange={() => toggleSilStaff("incompatible_staff", "preferred_staff", s.name)} />
                            {s.display_name || s.name}
                          </label>
                        ))}
                        {staff.length === 0 && <div className="text-[11px] text-slate-400">No staff found.</div>}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="details" className="space-y-3 mt-4">
                    <Field label="Property Name">
                      <Input value={editing.property_name} onChange={(e) => set("property_name", e.target.value)}
                             className="h-9 text-sm" data-testid="property-name-input" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="SIL Type">
                        <Select value={editing.sil_type} onValueChange={(v) => set("sil_type", v)}>
                          <SelectTrigger className="h-9 text-xs" data-testid="property-type"><SelectValue /></SelectTrigger>
                          <SelectContent>{SIL_TYPES.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Status">
                        <Select value={editing.status} onValueChange={(v) => set("status", v)}>
                          <SelectTrigger className="h-9 text-xs" data-testid="property-status"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="Active" className="text-xs">Active</SelectItem><SelectItem value="Inactive" className="text-xs">Inactive</SelectItem></SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="House Manager">
                        <Combobox options={staffOpts} value={editing.manager} onChange={(v) => set("manager", v)}
                                  placeholder="Select staff…" testid="property-manager" />
                      </Field>
                      <Field label="Number of Rooms">
                        <Input type="number" value={editing.num_rooms} onChange={(e) => set("num_rooms", Number(e.target.value))} className="h-9 text-xs" />
                      </Field>
                    </div>
                    <Field label="Address Line">
                      <Input value={editing.address_line1} onChange={(e) => set("address_line1", e.target.value)} className="h-9 text-xs" />
                    </Field>
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Suburb"><Input value={editing.suburb} onChange={(e) => set("suburb", e.target.value)} className="h-9 text-xs" /></Field>
                      <Field label="State"><Input value={editing.state} onChange={(e) => set("state", e.target.value)} className="h-9 text-xs" /></Field>
                      <Field label="Postcode"><Input value={editing.postcode} onChange={(e) => set("postcode", e.target.value)} className="h-9 text-xs" /></Field>
                    </div>
                    <Field label="Base Weekly Rent">
                      <Input type="number" value={editing.weekly_rent} onChange={(e) => set("weekly_rent", Number(e.target.value))} className="h-9 text-xs" />
                    </Field>
                    <Field label="Description"><Textarea value={editing.description} onChange={(e) => set("description", e.target.value)} className="text-xs min-h-[60px]" /></Field>
                    <Field label="Notes"><Textarea value={editing.notes} onChange={(e) => set("notes", e.target.value)} className="text-xs min-h-[60px]" data-testid="property-notes" /></Field>
                  </TabsContent>

                  <TabsContent value="tenancy" className="mt-4">
                    <ChildTable testid="tenancy" rows={editing.tenancies} onChange={(r) => set("tenancies", r)} addLabel="Add tenant"
                      cols={[
                        { key: "participant", label: "Participant", type: "participant", options: partOpts, w: "1.4fr" },
                        { key: "room", label: "Room", w: "0.7fr" },
                        { key: "start_date", label: "Move-in", type: "date", w: "1fr" },
                        { key: "weekly_rent", label: "Rent", type: "number", w: "0.7fr" },
                        { key: "status", label: "Status", type: "select", options: ["Active", "Pending", "Ended"], default: "Active", w: "0.9fr" },
                      ]} />
                  </TabsContent>

                  <TabsContent value="sil" className="mt-4 space-y-3" data-testid="sil-billing-tab">
                    <p className="text-xs text-slate-500">Recurring weekly SIL charges per tenant. Once the "Bill from" date is reached, each unbilled week flows into the next invoicing run (single & bulk) for that participant.</p>
                    {!can.has("invoicing.view") ? (
                      <div className="text-xs text-slate-400 py-6 text-center" data-testid="sil-no-accounts">SIL billing rates are managed by Accounts staff — you don't have an accounts role.</div>
                    ) : (editing.tenancies || []).filter((t) => t.participant).length === 0 ? (
                      <div className="text-xs text-slate-400 py-6 text-center">Add tenants on the Tenancy tab first.</div>
                    ) : (
                      <div className="space-y-3">
                        {(editing.tenancies || []).filter((t) => t.participant).map((t) => {
                          const c = silCharges[t.participant] || blankSil();
                          const last = (c.billed_periods || [])[(c.billed_periods || []).length - 1];
                          return (
                            <div key={t.participant} className="border rounded-lg p-3 space-y-2" data-testid={`sil-row-${t.participant}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-slate-800">{partName(t.participant)}</span>
                                <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                                  <input type="checkbox" checked={!!c.active} onChange={(e) => setSil(t.participant, "active", e.target.checked)} data-testid={`sil-active-${t.participant}`} />
                                  Active
                                </label>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <Field label="SIL Weekly Item">
                                  <Combobox options={silGroupOpts} value={c.item_group} onChange={(v) => { setSil(t.participant, "item_group", v); setSil(t.participant, "item_code", ""); loadGroupItems(t.participant, v); }} placeholder="Billing type" testid={`sil-group-${t.participant}`} />
                                </Field>
                                <Field label="NDIS Line Item">
                                  {!c.item_group ? (
                                    <div className="h-9 flex items-center text-[11px] text-slate-400">Pick a SIL Weekly item first</div>
                                  ) : (groupItems[t.participant] || []).length === 0 ? (
                                    <div className="h-9 flex items-center text-[11px] text-amber-600">No line items under this group</div>
                                  ) : (() => {
                                    const all = groupItems[t.participant] || [];
                                    const opts = all.filter((i) => i.in_date).length ? all.filter((i) => i.in_date) : all;
                                    return (
                                      <Select value={c.item_code || ""} onValueChange={(v) => {
                                        const it = opts.find((i) => i.item_code === v);
                                        setSil(t.participant, "item_code", v);
                                        if (it) { setSil(t.participant, "item_name", it.item_name || ""); if (!(Number(c.weekly_amount) > 0) && it.rate != null) setSil(t.participant, "weekly_amount", it.rate); }
                                      }}>
                                        <SelectTrigger className="h-9 text-xs" data-testid={`sil-item-${t.participant}`}><SelectValue placeholder="Select line item" /></SelectTrigger>
                                        <SelectContent>
                                          {opts.map((i) => (
                                            <SelectItem key={i.item_code} value={i.item_code} className="text-xs">
                                              {i.item_code}{i.item_name ? ` — ${i.item_name}` : ""}{i.rate != null ? ` ($${i.rate})` : ""}{i.rate != null && !i.in_date ? " (expired price)" : ""}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    );
                                  })()}
                                </Field>
                                <Field label="Ratio">
                                  <Select value={c.ratio || "1:1"} onValueChange={(v) => setSil(t.participant, "ratio", v)}>
                                    <SelectTrigger className="h-9 text-xs" data-testid={`sil-ratio-${t.participant}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>{["1:1", "1:2", "1:3", "1:4", "1:5"].map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}</SelectContent>
                                  </Select>
                                </Field>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <Field label="Weekly Amount ($)">
                                  <Input type="number" step="0.01" value={c.weekly_amount} onChange={(e) => setSil(t.participant, "weekly_amount", e.target.value)} className="h-9 text-xs" data-testid={`sil-amount-${t.participant}`} />
                                </Field>
                                <Field label="Bill From">
                                  <Input type="date" value={(c.start_date || "").slice(0, 10)} onChange={(e) => setSil(t.participant, "start_date", e.target.value)} className="h-9 text-xs" data-testid={`sil-start-${t.participant}`} />
                                </Field>
                                <Field label="Bill Until (optional)">
                                  <Input type="date" value={(c.end_date || "").slice(0, 10)} onChange={(e) => setSil(t.participant, "end_date", e.target.value)} className="h-9 text-xs" data-testid={`sil-end-${t.participant}`} />
                                </Field>
                              </div>
                              {last && <p className="text-[10px] text-slate-400">Last billed week: {String(last.week_start).slice(0, 10)} → {String(last.week_end).slice(0, 10)} ({(c.billed_periods || []).length} week(s) billed)</p>}
                            </div>
                          );
                        })}
                        {can.has("invoicing.billing_setup") && (
                          <Button onClick={saveSilBilling} disabled={silSaving} className="h-9 text-xs" data-testid="save-sil-billing">
                            {silSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <DollarSign className="h-3.5 w-3.5 mr-1" />} Save SIL billing
                          </Button>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="maint" className="mt-4">
                    <ChildTable testid="maint" rows={editing.maintenance} onChange={(r) => set("maintenance", r)} addLabel="Add maintenance"
                      cols={[
                        { key: "request_date", label: "Date", type: "date", default: todayISO(), w: "1fr" },
                        { key: "category", label: "Category", type: "select", options: ["General", "Plumbing", "Electrical", "Appliance", "Structural", "Garden", "Cleaning", "Safety", "Other"], default: "General", w: "1fr" },
                        { key: "priority", label: "Priority", type: "select", options: ["Low", "Medium", "High", "Urgent"], default: "Medium", w: "0.8fr" },
                        { key: "description", label: "Description", w: "1.4fr" },
                        { key: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Completed", "Cancelled"], default: "Open", w: "1fr" },
                      ]} />
                  </TabsContent>

                  <TabsContent value="whs" className="mt-4">
                    <ChildTable testid="whs" rows={editing.whs} onChange={(r) => set("whs", r)} addLabel="Add WHS entry"
                      cols={[
                        { key: "log_date", label: "Date", type: "date", default: todayISO(), w: "1fr" },
                        { key: "item", label: "Item / Hazard", w: "1.3fr" },
                        { key: "risk_level", label: "Risk", type: "select", options: ["Low", "Medium", "High", "Critical"], default: "Low", w: "0.9fr" },
                        { key: "action_taken", label: "Action Taken", w: "1.3fr" },
                        { key: "status", label: "Status", type: "select", options: ["Open", "Actioned", "Closed"], default: "Open", w: "0.9fr" },
                        { key: "due_date", label: "Due", type: "date", w: "1fr" },
                      ]} />
                  </TabsContent>

                  <TabsContent value="insp" className="mt-4">
                    <ChildTable testid="insp" rows={editing.inspections} onChange={(r) => set("inspections", r)} addLabel="Add inspection"
                      cols={[
                        { key: "inspection_date", label: "Date", type: "date", default: todayISO(), w: "1fr" },
                        { key: "inspection_type", label: "Type", type: "select", options: ["Routine", "Entry", "Exit", "Fire Safety", "Maintenance", "Other"], default: "Routine", w: "1fr" },
                        { key: "inspector", label: "Inspector", w: "1.1fr" },
                        { key: "outcome", label: "Outcome", w: "1.3fr" },
                        { key: "status", label: "Status", type: "select", options: ["Scheduled", "Completed", "Overdue"], default: "Completed", w: "1fr" },
                        { key: "next_due", label: "Next Due", type: "date", w: "1fr" },
                      ]} />
                  </TabsContent>

                  <TabsContent value="corr" className="mt-4">
                    <ChildTable testid="corr" rows={editing.correspondence} onChange={(r) => set("correspondence", r)} addLabel="Add correspondence"
                      cols={[
                        { key: "corr_date", label: "Date", type: "date", default: todayISO(), w: "1fr" },
                        { key: "direction", label: "Direction", type: "select", options: ["Incoming", "Outgoing"], default: "Incoming", w: "1fr" },
                        { key: "party", label: "From / To", w: "1.2fr" },
                        { key: "subject", label: "Subject", w: "1.4fr" },
                        { key: "notes", label: "Notes", w: "1.4fr" },
                      ]} />
                  </TabsContent>
                </Tabs>
              </div>

              <div className="px-6 py-4 border-t sticky bottom-0 bg-white flex items-center justify-between gap-2">
                {editName && can.has("properties.delete") ? (
                  <Button variant="outline" onClick={() => setDelName(editName)} className="text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="property-delete-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                ) : <span />}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setEditing(null); setEditName(null); }} className="text-xs">Cancel</Button>
                  {can.has("properties.edit") && (
                    <Button onClick={save} disabled={saving} className="text-xs" data-testid="property-save-btn">
                      {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                      {editName ? "Save changes" : "Create property"}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delName} onOpenChange={(o) => !o && setDelName(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this property?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the SIL house and all its logs. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="text-xs bg-red-600 hover:bg-red-700" data-testid="property-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
